import React, { useId } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import type { EditorGroup, ScenarioId } from '../../types/quote';
import { easeOut } from '../../utils/motion';
import { FieldTag } from './FieldTag';
type EditorFieldGroupProps = {
  group: EditorGroup;
  open: boolean;
  onToggle: () => void;
  scenarioId: ScenarioId;
};
export function EditorFieldGroup({
  group,
  open,
  onToggle,
  scenarioId
}: EditorFieldGroupProps) {
  const reduce = useReducedMotion();
  const panelId = useId();
  return <div className="rounded-[22px] bg-white ring-1 ring-navy-100" data-id="element-16" data-mp="2qd08qk07t03q">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="flex w-full items-center justify-between gap-3 rounded-[22px] px-4 py-3 text-left duration-150 ease-out hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-flame/30 motion-safe:transition-colors" data-id="element-17" data-mp="2qfcpwuy5f6fq">
        <span className="text-sm font-extrabold" data-id="element-18" data-mp="b0xctub86fcs">{group.title}</span>
        <span className="flex items-center gap-2" data-id="element-19" data-mp="g1v2st2h8dou">
          <span className="text-xs font-bold text-navy-500" data-id="element-20" data-mp="fzid1tqh8z18">{group.fields.length} fields</span>
          <ChevronDownIcon className={`h-4 w-4 duration-200 ease-out motion-safe:transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-21" data-mp="16f6siic67pt" />
        </span>
      </button>

      <AnimatePresence initial={false} data-id="element-22" data-mp="ys32t2avqo0t">
        {open && <motion.div id={panelId} initial={reduce ? false : {
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={reduce ? undefined : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: easeOut
      }} className="overflow-hidden" data-id="element-23" data-mp="yrjk3jdenvlq">
            <ul className="space-y-3 px-4 pb-4 pt-1" data-id="element-24" data-mp="1hikgq0r1zf24">
              {group.fields.map(field => {
            const inputId = `${panelId}-${field.id}`;
            const value = field.scenarioValues ? field.scenarioValues[scenarioId] : field.value;
            return <li key={field.id} data-id="element-25" data-mp="1mdn8whc38db1">
                    <div className="mb-1 flex items-center justify-between gap-2" data-id="element-26" data-mp="1id39f741kxlq">
                      <label htmlFor={inputId} className="text-xs font-bold text-navy-700" data-id="element-27" data-mp="1dfb7dk9onv3t">
                        {field.label}
                      </label>
                      <FieldTag source={field.source} data-id="element-28" data-mp="18hrt4kczjibv" />
                    </div>
                    <input id={inputId} readOnly value={value} aria-readonly="true" className={`h-10 w-full rounded-full px-4 text-sm font-bold tabular-nums focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-flame/30 ${field.source === 'lo' ? 'border border-navy-200 bg-white text-navy' : 'border border-dashed border-navy-200 bg-navy-50 text-navy-500'}`} data-id="element-29" data-mp="13nf9oabie704" />
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}